// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import type { WorkOrder } from '@evolvemep/api-ts';
import { Card, Radio, Stack, Text, TextInput } from '@mantine/core';

import type { ModalRegistryManagedProps } from 'components/Mantine/Modals/createModalRegistry';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { getDisplayNameWO } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';
import type { WorkOrderAction } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useCanEditWorkOrder';
import { useWorkOrderModals } from 'modules/Shop/WorkOrders/WorkOrdersPage/modalRegistries';
import { useWorkOrders } from 'modules/Shop/WorkOrders/WorkOrdersPage/useWorkOrders';

type Props = ModalRegistryManagedProps & {
  workOrder: WorkOrder;
  onWorkOrderActioned: (action: WorkOrderAction) => void;
};

/** Prompt for accepting/denying a cancellation request. */
export const CancelWorkOrderRequestedModal = ({ onClose, workOrder, onWorkOrderActioned, ...props }: Props) => {
  const { openModal } = useWorkOrderModals();
  const navigate = useEvolveNavigate(getDisplayNameWO(workOrder), true);
  const { acceptCancelRequest, denyCancelRequest, mutationRunning } = useWorkOrders();

  const [selectedAction, setSelectedAction] = useState<'cancel' | 'dont-cancel' | 'idk'>();
  const [reason, setReason] = useState('');

  const shouldBlock = isNotNil(workOrder.productionStartedOn);

  const onSubmit = async () => {
    if (selectedAction === 'dont-cancel') {
      await denyCancelRequest(workOrder.workOrderId, reason).then(() => onWorkOrderActioned('denyCancel'));
    } else if (selectedAction === 'cancel') {
      if (shouldBlock) {
        openModal('blockWorkOrderModal', {
          workOrder,
          onWorkOrderActioned: () => onWorkOrderActioned('acceptCancel'),
          cancellationRequest: true,
        });
      } else {
        await acceptCancelRequest(workOrder.workOrderId, []).then(() => onWorkOrderActioned('acceptCancel'));
        void navigate('/work-orders');
      }
    }
    onClose();
  };

  const getConfirmButtonText = () => {
    if (selectedAction === 'cancel') return shouldBlock ? 'Next' : 'Save';
    if (selectedAction === 'idk') return 'Close';
    return 'Save';
  };

  return (
    <WrappedModal
      title="Request to cancel work order"
      onClose={onClose}
      withCloseButton={false}
      size="lg"
      onConfirm={onSubmit}
      withCancelButton={false}
      buttonVariant={
        selectedAction === 'dont-cancel' || (selectedAction === 'cancel' && shouldBlock) ? 'filled' : 'light'
      }
      buttonColor={selectedAction === 'cancel' ? 'red' : undefined}
      disabled={mutationRunning || isNil(selectedAction) || (selectedAction === 'dont-cancel' && !reason.trim())}
      confirmationText={getConfirmButtonText()}
      closeOnConfirm={false}
      {...props}
    >
      <Text fz="sm">
        <b>{workOrder.workOrderStateCreatedBy?.nameOrEmail ?? workOrder.projectName}</b> requested to cancel work order{' '}
        <b>{getDisplayNameWO(workOrder)}</b> with the following reason:
      </Text>
      <Card fz="sm" bg="gray.1" p="xs">
        <Text>{workOrder.workOrderStateComments}</Text>
      </Card>
      <Radio.Group value={selectedAction} onChange={(v) => setSelectedAction(v as typeof selectedAction)}>
        <Stack>
          <Radio
            value="cancel"
            label={`Yes, ${shouldBlock ? 'block' : 'cancel'} work order`}
            description={
              shouldBlock
                ? 'Put the work order in a blocked status and allow changes to release again when ready.'
                : 'Put the work order in a cancelled status. It can be resubmitted later.'
            }
            disabled={mutationRunning}
          />
          <Radio
            value="dont-cancel"
            label="No, don't cancel"
            description="Provide a reason for dismissing the cancellation request."
            disabled={mutationRunning}
          />
          {selectedAction === 'dont-cancel' && (
            <TextInput
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason"
              autoFocus
              data-autofocus
              disabled={mutationRunning}
            />
          )}
          <Radio
            value="idk"
            label="I don't know yet"
            description="Come back and make a decision at a later time."
            disabled={mutationRunning}
          />
        </Stack>
      </Radio.Group>
    </WrappedModal>
  );
};
